/*
 * aura-builds.js: Instagram posts in the product page's builds row.
 * Once the row is near the screen, asks the Gallery service for this color's builds (?color=<title>)
 * and fills the rail with the ones that came from Instagram, each a link out to its post, built from
 * the server-rendered <template>. Only https Instagram post links and https images are used, and a
 * row's perma must match this product's handle. The section is rendered hidden and only shown once
 * there are posts; if the feed fails or has none, it stays hidden.
 */
(() => {
  const track = (n, p) => window.aura && window.aura.track(n, p);
  const IG = /^https:\/\/(www\.)?instagram\.com\//;
  /* instagram.com/<user>/reel/<code>/ carries the poster; /p/<code>/ does not */
  const handleOf = (url) => {
    const first = new URL(url).pathname.split('/').filter(Boolean)[0] || '';
    return first && !['p', 'reel', 'reels', 'tv'].includes(first) ? `@${first}` : '';
  };

  class AuraBuilds extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '600px 0px' });
      /* hidden means no box to intersect, so watch the section wrapper instead */
      this.observer.observe(this.parentElement || this);
    }

    disconnectedCallback() { this.observer?.disconnect(); }

    async load() {
      let rows;
      try {
        const res = await fetch(this.dataset.feed, { credentials: 'omit' });
        if (!res.ok) throw new Error(`gallery ${res.status}`);
        rows = (await res.json()).array;
      } catch (e) { return; }

      const handle = this.dataset.handle;
      const posts = (Array.isArray(rows) ? rows : [])
        .filter((r) => r && IG.test(r.sourceUrl || '') && /^https:\/\//.test(r.url || '') && (!r.perma || r.perma === handle))
        .slice(0, 12);
      if (!posts.length) return;

      const s = JSON.parse(this.querySelector('[data-builds-strings]').textContent);
      const tpl = this.querySelector('template[data-builds-ig-tile]');
      const rail = this.querySelector('[data-builds-rail]');
      const frag = document.createDocumentFragment();
      posts.forEach((post, i) => {
        const li = tpl.content.firstElementChild.cloneNode(true);
        const link = li.querySelector('a');
        const who = handleOf(post.sourceUrl);
        const car = String(post.title || '').trim();
        link.href = post.sourceUrl;
        link.setAttribute('aria-label', (who ? s.label : s.label_anon).replace('[car]', car || s.build).replace('[who]', who));
        link.addEventListener('click', () => track('aura_builds_ig_post', { position: i + 1, color: handle }));
        li.querySelector('[data-who]').textContent = who || s.instagram;
        li.querySelector('[data-car]').textContent = car;
        const img = li.querySelector('img');
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
        const resized = window.auraImg ? window.auraImg.srcset(post.url, [300, 450, 600, 900]) : '';
        if (resized) { window.auraImg.fallback(img, post.url); img.srcset = resized; }
        img.src = resized ? window.auraImg.src(post.url, 600) : post.url;
        frag.append(li);
      });
      rail.replaceChildren(frag);
      this.hidden = false;

      const note = this.querySelector('[data-builds-note]');
      if (note) note.textContent = (posts.length === 1 ? s.note_one : s.note_other).replace('[count]', posts.length);
      const nav = this.querySelector('[data-builds-nav]');
      if (nav) nav.hidden = rail.children.length <= 3;
    }
  }

  if (!customElements.get('aura-builds')) customElements.define('aura-builds', AuraBuilds);
})();
